import { useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { getIniciais } from '@/lib/avatarIniciais';

export interface MultiSelectItem {
  id: string;
  nome: string;
  avatarUrl?: string | null;
}

interface MultiSelectComboboxProps {
  items: MultiSelectItem[];
  value: MultiSelectItem[];
  onChange: (value: MultiSelectItem[]) => void;
  placeholder?: string;
  emptyLabel?: string;
  // Catálogos livres (habilidades, cursos) deixam criar um item novo digitando; catálogos
  // fechados (ex: usuários da empresa) não fazem sentido "criar" — desabilita a opção.
  allowCreate?: boolean;
  /** Sobrepõe o tamanho padrão do botão-gatilho — usado em barras de filtro compactas, pra
   *  bater com a altura dos Selects ao lado (ele não tem altura fixa por padrão). */
  triggerClassName?: string;
}

export function MultiSelectCombobox({
  items,
  value,
  onChange,
  placeholder = 'Selecionar...',
  emptyLabel = 'Nenhum item encontrado.',
  allowCreate = true,
  triggerClassName,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');

  const selecionadosIds = new Set(value.map((v) => v.id));
  const disponiveis = items.filter((item) => !selecionadosIds.has(item.id));
  const buscaNormalizada = busca.trim().toLowerCase();
  const jaExisteExato =
    items.some((item) => item.nome.toLowerCase() === buscaNormalizada) ||
    value.some((item) => item.nome.toLowerCase() === buscaNormalizada);

  const adicionar = (item: MultiSelectItem) => {
    onChange([...value, item]);
    setBusca('');
  };

  const criarNovo = () => {
    const nome = busca.trim();
    if (!nome) return;
    adicionar({ id: `novo:${nome}`, nome });
  };

  const remover = (id: string) => {
    onChange(value.filter((v) => v.id !== id));
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn('w-full justify-start font-normal text-muted-foreground', triggerClassName)}
          >
            <Plus className="mr-2 h-4 w-4" />
            {placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Buscar ou criar..." value={busca} onValueChange={setBusca} />
            <CommandList>
              {disponiveis.filter((item) => item.nome.toLowerCase().includes(buscaNormalizada)).length === 0 && !busca && (
                <CommandEmpty>{emptyLabel}</CommandEmpty>
              )}
              <CommandGroup>
                {disponiveis
                  .filter((item) => item.nome.toLowerCase().includes(buscaNormalizada))
                  .map((item) => (
                    <CommandItem key={item.id} value={item.nome} onSelect={() => adicionar(item)}>
                      <Check className={cn('mr-2 h-4 w-4 opacity-0')} />
                      {item.avatarUrl !== undefined && (
                        <Avatar className="mr-2 h-5 w-5">
                          <AvatarImage src={item.avatarUrl ?? undefined} alt={item.nome} />
                          <AvatarFallback className="text-[9px]">{getIniciais(item.nome)}</AvatarFallback>
                        </Avatar>
                      )}
                      {item.nome}
                    </CommandItem>
                  ))}
                {allowCreate && busca.trim() !== '' && !jaExisteExato && (
                  <CommandItem value={`__criar__${busca}`} onSelect={criarNovo}>
                    <Plus className="mr-2 h-4 w-4" />
                    Criar "{busca.trim()}"
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((item) => (
            <Badge key={item.id} variant="secondary" className="gap-1.5 py-1 pl-1">
              {item.avatarUrl !== undefined && (
                <Avatar className="h-4 w-4">
                  <AvatarImage src={item.avatarUrl ?? undefined} alt={item.nome} />
                  <AvatarFallback className="text-[8px]">{getIniciais(item.nome)}</AvatarFallback>
                </Avatar>
              )}
              {item.nome}
              <button type="button" onClick={() => remover(item.id)} className="ml-1 rounded-full outline-none hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
